import { useEffect, useState } from "react";
import {
    ShieldCheck,
    Loader2,
    DollarSign,
    Mail,
    Users,
    Inbox,
    Newspaper,
    Sparkles,
    RefreshCw,
    Send,
    LogOut,
    Eye,
    BarChart3,
    Radio,
    CheckCircle2,
    AlertCircle,
    Rss,
    Map,
    Target,
    Send as SendIcon,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    adminLogin,
    adminOverview,
    adminOutbox,
    adminFlushOutbox,
    adminRecentLeads,
    adminTransactions,
    adminFireNewsletter,
    adminFireFirmDigest,
    adminDistributionStatus,
    adminDistributionPingAll,
    adminVisitors,
    adminOutreachGenerate,
    adminOutreachPackets,
    adminOutreachSend,
    adminOutreachMarkSentManual,
    adminOutreachStats,
    adminOutreachSendAllDrafts,
    adminRefund,
    adminDripRun,
    getAdminToken,
    setAdminToken,
    clearAdminToken,
} from "@/lib/adminApi";

export default function AdminDashboard() {
    const [token, setToken] = useState(getAdminToken());
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [outbox, setOutbox] = useState({ items: [] });
    const [leads, setLeads] = useState({ items: [] });
    const [txs, setTxs] = useState({ items: [] });
    const [dist, setDist] = useState(null);
    const [visitors, setVisitors] = useState(null);
    const [outreachStats, setOutreachStats] = useState(null);
    const [outreachPackets, setOutreachPackets] = useState([]);
    const [outreachInput, setOutreachInput] = useState("");
    const [outreachBusy, setOutreachBusy] = useState(false);
    const [pingingAll, setPingingAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [authError, setAuthError] = useState("");

    useEffect(() => {
        document.title = "HPM Admin · Higher Power Movement";
    }, []);

    const authed = !!token;

    const refresh = async () => {
        if (!authed) return;
        setLoading(true);
        try {
            const [o, ob, ld, tx, ds, vs, os_, op] = await Promise.all([
                adminOverview().catch(() => null),
                adminOutbox().catch(() => ({ items: [] })),
                adminRecentLeads(25).catch(() => ({ items: [] })),
                adminTransactions(50).catch(() => ({ items: [] })),
                adminDistributionStatus().catch(() => null),
                adminVisitors().catch(() => null),
                adminOutreachStats().catch(() => null),
                adminOutreachPackets(null, 100).catch(() => ({ packets: [] })),
            ]);
            if (!o) {
                // token rejected
                clearAdminToken();
                setToken("");
                setAuthError("Token rejected. Try again.");
                return;
            }
            setOverview(o);
            setOutbox(ob);
            setLeads(ld);
            setTxs(tx);
            setDist(ds);
            setVisitors(vs);
            setOutreachStats(os_);
            setOutreachPackets(op?.packets || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authed) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed]);

    const submitToken = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const t = String(fd.get("token") || "").trim();
        if (!t) {
            setAuthError("Enter your admin token.");
            return;
        }
        setAuthError("");
        try {
            setAdminToken(t);
            await adminLogin(t);
            setToken(t);
            toast.success("Authenticated.");
        } catch {
            clearAdminToken();
            setAuthError("Token rejected.");
        }
    };

    const logout = () => {
        clearAdminToken();
        setToken("");
        setOverview(null);
    };

    const fireNewsletter = async (dry) => {
        if (!dry) {
            const subs = ov?.newsletter.active_subscribers || 0;
            const provider = ov?.provider_configured.resend ? "RESEND (live email)" : "OUTBOX (queued)";
            const ok = window.confirm(
                `FIRE NEWSLETTER — about to send the weekly digest to ${subs} active subscribers via ${provider}. Continue?`,
            );
            if (!ok) return;
        }
        try {
            const r = await adminFireNewsletter(dry);
            toast.success(
                dry
                    ? `Dry-run · ${r.subscriber_count} subs · provider=${r.provider}`
                    : `Newsletter queued for ${r.subscriber_count} subscribers.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    const fireFirmDigest = async (dry) => {
        if (!dry) {
            const firms = ov?.firms.active + ov?.firms.trial || 0;
            const provider = ov?.provider_configured.resend ? "RESEND (live email)" : "OUTBOX (queued)";
            const ok = window.confirm(
                `FIRE FIRM DIGEST — about to email ${firms} firms with alerts_enabled via ${provider}. Continue?`,
            );
            if (!ok) return;
        }
        try {
            const r = await adminFireFirmDigest(dry);
            toast.success(
                dry
                    ? `Dry-run · ${r.firm_count} firms · provider=${r.provider}`
                    : `Firm digest queued for ${r.firm_count} firms.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    const flushOutbox = async () => {
        try {
            const r = await adminFlushOutbox();
            toast.success(`Flushing ${r.queued} queued items…`);
            setTimeout(refresh, 1500);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Flush failed");
        }
    };

    const pingAll = async () => {
        const ok = window.confirm(
            "PING ALL — about to submit every blog post + 1,020 SEO pages to IndexNow (Bing/Yandex/DDG) and ping Google sitemap. Continue?",
        );
        if (!ok) return;
        setPingingAll(true);
        try {
            const r = await adminDistributionPingAll();
            toast.success(`Pinged ${r.url_count} URLs across IndexNow + Google.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ping failed");
        } finally {
            setPingingAll(false);
        }
    };

    const refundTx = async (tx) => {
        if (!tx?.session_id) {
            toast.error("This transaction has no session_id to refund.");
            return;
        }
        const dollars = (tx.amount || 0).toFixed(2);
        const ok = window.confirm(
            `REFUND $${dollars} for session ${tx.session_id}?\n\nThis hits Stripe's live Refund API and cannot be undone.`,
        );
        if (!ok) return;
        try {
            const res = await adminRefund({
                session_id: tx.session_id,
                reason: "requested_by_customer",
            });
            toast.success(`Refunded — ${res.refund_id || "ok"} · ${res.status || ""}`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refund failed");
        }
    };

    const runDrip = async (dry) => {
        if (!dry) {
            const ok = window.confirm(
                "RUN DRIP — about to send any unsent Day 3/7/14/21 emails to all trial firms via Resend (or queue to outbox). Continue?",
            );
            if (!ok) return;
        }
        try {
            const r = await adminDripRun(dry);
            toast.success(
                dry
                    ? `Dry-run · ${r.firms_scanned} firms scanned · ${r.stages_fired} stages would fire`
                    : `Drip processed · ${r.firms_scanned} firms · ${r.stages_fired} emails sent`,
            );
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Drip run failed");
        }
    };

    if (!authed) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2 flex items-center gap-2">
                        <ShieldCheck size={11} /> ADMIN · HIGHER POWER MOVEMENT
                    </div>
                    <h1 className="font-anton text-5xl uppercase leading-[0.95] mb-2">
                        Run the <span className="text-[#FF3B30]">whole machine</span>.
                    </h1>
                    <p className="font-sans text-white/70 text-sm mb-6">
                        This dashboard is gated by your <code>HPM_ADMIN_TOKEN</code>. Set that env
                        var on your deploy, then paste it below.
                    </p>
                    <form onSubmit={submitToken}>
                        <input
                            name="token"
                            type="password"
                            placeholder="Admin token"
                            className="w-full bg-transparent border-2 border-white/30 px-3 py-3 font-mono text-sm text-white placeholder-white/40 focus:border-white outline-none"
                            data-testid="admin-token-input"
                        />
                        {authError && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] mt-2">
                                {authError}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn-brutal btn-brutal-red w-full justify-center mt-4"
                            data-testid="admin-submit"
                        >
                            ENTER →
                        </button>
                    </form>
                </div>
                <Footer disclaimer="" />
            </div>
        );
    }

    const ov = overview;
    const tabs = [
        { key: "overview", label: "OVERVIEW", icon: BarChart3 },
        { key: "outreach", label: "OUTREACH", icon: Target },
        { key: "visitors", label: "VISIBILITY", icon: Eye },
        { key: "leads", label: "LEADS", icon: Inbox },
        { key: "newsletter", label: "NEWSLETTER", icon: Mail },
        { key: "distribution", label: "DISTRIBUTION", icon: Radio },
        { key: "outbox", label: "OUTBOX", icon: Send },
        { key: "transactions", label: "TRANSACTIONS", icon: DollarSign },
    ];

    const parseOutreachInput = (raw) => {
        // Accepts CSV-ish input: firm_name, contact_email, contact_name, state, practice
        // One firm per line. Tolerant of TSV / comma-separated / pipe-separated.
        const out = [];
        const lines = (raw || "").split(/\r?\n/);
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || trimmed.startsWith("#")) continue;
            const parts = trimmed.split(/\s*[,|\t]\s*/);
            if (parts.length < 2) continue;
            const [firm_name, contact_email, contact_name = "", state = "", practice = ""] = parts;
            if (!firm_name || !contact_email.includes("@")) continue;
            out.push({ firm_name, contact_email, contact_name, state, practice });
        }
        return out;
    };

    const generateOutreach = async (dryRun) => {
        const targets = parseOutreachInput(outreachInput);
        if (!targets.length) {
            toast.error("Paste at least one firm. Format: Firm Name, email@firm.com, Contact Name, State, Practice");
            return;
        }
        if (targets.length > 50) {
            toast.error("Max 50 firms per batch.");
            return;
        }
        setOutreachBusy(true);
        try {
            const res = await adminOutreachGenerate({
                targets,
                dry_run: dryRun,
                public_base: window.location.origin,
            });
            toast.success(`${res.count} pitches ${dryRun ? "drafted" : "sent"}.`);
            setOutreachInput("");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Outreach generation failed");
        } finally {
            setOutreachBusy(false);
        }
    };

    const sendPacket = async (packetId) => {
        try {
            await adminOutreachSend(packetId);
            toast.success("Packet sent");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    const markPacketSentManual = async (packetId) => {
        try {
            await adminOutreachMarkSentManual(packetId);
            toast.success("Marked as sent");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const sendAllDrafts = async () => {
        if (!window.confirm("Send all draft pitches now? This will fire emails via Resend (or queue all to outbox if no key).")) return;
        setOutreachBusy(true);
        try {
            const r = await adminOutreachSendAllDrafts();
            toast.success(`Processed ${r.drafts_processed} drafts — ${r.sent} sent, ${r.queued} queued.`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk send failed");
        } finally {
            setOutreachBusy(false);
        }
    };

    const copyPacket = async (pkt) => {
        const text = `Subject: ${pkt.subject}\n\nTo: ${pkt.contact_email}\n\n${pkt.body}`;
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard — paste into Gmail / LinkedIn");
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1500px] mx-auto px-6 sm:px-10 py-10" data-testid="admin-dashboard">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                            ADMIN CONTROL · ALPHA COVENANT HOLDINGS
                        </div>
                        <h1 className="font-anton text-5xl uppercase leading-none mt-1">
                            HPM Dashboard
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={refresh} className="btn-brutal text-xs" data-testid="admin-refresh">
                            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}{" "}
                            REFRESH
                        </button>
                        <button onClick={logout} className="btn-brutal text-xs" data-testid="admin-logout">
                            <LogOut size={12} /> LOGOUT
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div
                    className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 border-2 border-white/20 mb-8"
                    data-testid="admin-tabs"
                >
                    {tabs.map(({ key, label, icon: Icon }) => {
                        const active = tab === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setTab(key)}
                                data-testid={`admin-tab-${key}`}
                                className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2 transition-colors ${active ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
                            >
                                <Icon size={11} /> {label}
                            </button>
                        );
                    })}
                </div>

                {tab === "overview" && ov && (
                    <div data-testid="tab-admin-overview">
                        {/* Provider readiness */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border-2 border-white/20 mb-6">
                            {[
                                { k: "stripe", l: "STRIPE" },
                                { k: "resend", l: "RESEND" },
                                { k: "admin_token", l: "ADMIN TOKEN" },
                                { k: "public_base_url", l: "PUBLIC URL" },
                                { k: "from_email", l: "FROM EMAIL" },
                            ].map((p, i) => {
                                const v = ov.provider_configured[p.k];
                                const ok = !!v;
                                const display =
                                    typeof v === "boolean" ? (ok ? "READY" : "MISSING") : v || "MISSING";
                                return (
                                    <div
                                        key={p.k}
                                        className={`p-4 border-white/20 lg:[&:not(:nth-child(5))]:border-r-2 [&:not(:last-child)]:border-b-2 lg:[&:nth-child(-n+5)]:border-b-0 ${ok ? "" : "bg-[#FF3B30]/10"}`}
                                        data-testid={`provider-${p.k}`}
                                    >
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                            {p.l}
                                        </div>
                                        <div
                                            className={`font-mono text-xs mt-1 break-all ${ok ? "text-[#FFCC00]" : "text-[#FF3B30]"}`}
                                        >
                                            {display}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* KPI cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-white/20">
                            <KPI
                                label="LEADS TODAY"
                                value={ov.leads.today}
                                sub={`${ov.leads.week} this week · ${ov.leads.total} all-time`}
                                accent="#FF3B30"
                            />
                            <KPI
                                label="REVENUE TODAY"
                                value={`$${ov.revenue.today.toFixed(0)}`}
                                sub={`$${ov.revenue.week.toFixed(0)} this week · $${ov.revenue.total.toFixed(0)} all-time`}
                                accent="#FFCC00"
                            />
                            <KPI
                                label="FIRMS"
                                value={ov.firms.total}
                                sub={`${ov.firms.active} paid · ${ov.firms.trial} on trial`}
                                accent="#FF3B30"
                            />
                            <KPI
                                label="NEWSLETTER"
                                value={ov.newsletter.active_subscribers}
                                sub={`${ov.newsletter.outbox_queued} in outbox`}
                                accent="#FFCC00"
                            />
                        </div>

                        {/* Revenue by purpose */}
                        <div className="mt-6 border-2 border-white/20 p-5" data-testid="revenue-breakdown">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3">
                                REVENUE BY PRODUCT (PAID-IN-FULL · ALL TIME)
                            </div>
                            {Object.keys(ov.revenue.by_purpose || {}).length === 0 ? (
                                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                                    NO PAID TRANSACTIONS YET. ONCE STRIPE IS LIVE, MONEY LANDS HERE.
                                </div>
                            ) : (
                                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {Object.entries(ov.revenue.by_purpose).map(([k, v]) => (
                                        <li
                                            key={k}
                                            className="border-2 border-white/10 p-3 flex justify-between items-center"
                                        >
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
                                                {k.replace(/_/g, " ").replace(/^hpm /i, "")}
                                            </span>
                                            <span className="font-anton text-xl text-[#FF3B30]">${v.toFixed(0)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Top posts + tier breakdown + spotlights */}
                        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="border-2 border-white/20 p-5">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3">
                                    TOP POSTS (BY VIEWS)
                                </div>
                                <ul className="space-y-2">
                                    {ov.top_posts.map((p) => (
                                        <li key={p.slug} className="flex justify-between items-center gap-3">
                                            <div className="font-sans text-sm truncate">{p.title}</div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 flex items-center gap-3 whitespace-nowrap">
                                                <span className="flex items-center gap-1">
                                                    <Eye size={10} /> {p.view_count || 0}
                                                </span>
                                                <span className="text-[#FF3B30]">
                                                    {p.share_count_total || 0} ↗
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="border-2 border-white/20 p-5">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3">
                                    LEAD TIER BREAKDOWN
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {["S", "A", "B"].map((t) => {
                                        const n = ov.leads.tier_breakdown?.[t] || 0;
                                        const color =
                                            t === "S" ? "text-[#FF3B30] border-[#FF3B30]" : t === "A" ? "text-[#FFCC00] border-[#FFCC00]" : "text-white border-white/40";
                                        return (
                                            <div key={t} className={`border-2 ${color} p-3 text-center`}>
                                                <div className="font-mono text-[10px] uppercase tracking-[0.3em]">
                                                    TIER {t}
                                                </div>
                                                <div className="font-anton text-4xl mt-1">{n}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                    ACTIVE SPOTLIGHTS · {ov.active_spotlights.length}
                                </div>
                                <ul className="mt-2 space-y-1">
                                    {ov.active_spotlights.map((s) => (
                                        <li key={s.slot_key} className="font-mono text-xs text-white/70 flex items-center gap-2">
                                            <Sparkles size={10} className="text-[#FFCC00]" />
                                            <span>{s.label}</span>
                                            <span className="text-white/40">· ends {s.ends_at?.slice(0, 10)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "outreach" && (
                    <div data-testid="tab-admin-outreach" className="space-y-6">
                        {/* Funnel stats */}
                        <div className="grid grid-cols-2 md:grid-cols-5 border-2 border-white/20">
                            {[
                                { label: "PITCHES", value: outreachStats?.total_packets ?? 0, accent: "text-white" },
                                { label: "SENT", value: outreachStats?.sent ?? 0, accent: "text-white" },
                                { label: "CLICKED", value: outreachStats?.clicked ?? 0, accent: "text-[#FFCC00]" },
                                { label: "CONVERTED", value: outreachStats?.converted ?? 0, accent: "text-[#00FF85]" },
                                {
                                    label: "OVERALL %",
                                    value: `${outreachStats?.overall_conversion_pct ?? 0}%`,
                                    accent: "text-[#FF3B30]",
                                },
                            ].map((m, i) => (
                                <div
                                    key={m.label}
                                    className={`p-6 ${i < 4 ? "border-r-2 border-white/20" : ""}`}
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                        {m.label}
                                    </div>
                                    <div className={`font-anton text-4xl leading-none ${m.accent}`} data-testid={`outreach-stat-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                        {m.value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Generator */}
                        <div className="border-2 border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">
                                FIRM OUTREACH HUNTER · REVENUE WEAPON
                            </div>
                            <h2 className="font-anton text-3xl uppercase leading-none mb-3">
                                Turn 20 firms into paying customers this week.
                            </h2>
                            <p className="font-sans text-white/70 leading-relaxed mb-4 text-sm">
                                Paste firms below — one per line — as <span className="font-mono text-white">Firm Name, email, Contact Name, State, Practice</span>.
                                HPM Claude will draft a personalised pitch for each one, tagged with a unique
                                referral URL that pins every conversion back to this pitch.
                            </p>
                            <textarea
                                value={outreachInput}
                                onChange={(e) => setOutreachInput(e.target.value)}
                                rows={8}
                                placeholder={`Williams & Park Civil Rights, twilliams@williamspark.com, Tasha, California, civil rights\nReed Defense Group, mreed@reedlegal.com, Marcus, New York, wrongful conviction`}
                                className="w-full bg-black border-2 border-white/20 p-3 font-mono text-xs text-white/90 placeholder-white/30 focus:border-[#FF3B30] outline-none"
                                data-testid="outreach-input"
                            />
                            <div className="flex flex-wrap gap-3 mt-4">
                                <button
                                    onClick={() => generateOutreach(true)}
                                    disabled={outreachBusy}
                                    className="btn-brutal text-[10px]"
                                    data-testid="outreach-draft-btn"
                                >
                                    {outreachBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                    DRAFT PITCHES (NO SEND)
                                </button>
                                <button
                                    onClick={() => generateOutreach(false)}
                                    disabled={outreachBusy}
                                    className="btn-brutal btn-brutal-red text-[10px]"
                                    data-testid="outreach-send-btn"
                                >
                                    <SendIcon size={12} /> DRAFT + SEND VIA RESEND
                                </button>
                            </div>
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-3">
                                IF RESEND_API_KEY IS UNSET, "SEND" QUEUES TO OUTREACH_OUTBOX FOR LATER.
                            </p>
                        </div>

                        {/* Packets list */}
                        <div className="border-2 border-white/20">
                            <div className="p-6 border-b-2 border-white/10 flex items-center justify-between flex-wrap gap-2">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                    YOUR PITCHES · {outreachPackets.length} TOTAL
                                </div>
                                {outreachPackets.some((p) => p.status === "draft") && (
                                    <button
                                        onClick={sendAllDrafts}
                                        disabled={outreachBusy}
                                        className="btn-brutal btn-brutal-red text-[10px]"
                                        data-testid="outreach-bulk-send-btn"
                                    >
                                        {outreachBusy ? (
                                            <Loader2 size={12} className="animate-spin" />
                                        ) : (
                                            <>
                                                <SendIcon size={11} /> SEND ALL DRAFTS NOW
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            {outreachPackets.length === 0 ? (
                                <div className="p-6 font-mono text-xs text-white/40">
                                    NO PITCHES YET. PASTE FIRMS ABOVE TO START.
                                </div>
                            ) : (
                                <ul className="divide-y-2 divide-white/10">
                                    {outreachPackets.map((p) => (
                                        <li key={p.packet_id} className="p-6" data-testid={`outreach-packet-${p.code}`}>
                                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                                <div>
                                                    <div className="font-anton text-xl uppercase">{p.firm_name}</div>
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                                        {p.contact_email} · {p.state || "?"} · {p.practice || "?"}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    <span
                                                        className={`font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 border-2 ${
                                                            p.status === "converted"
                                                                ? "border-[#00FF85] text-[#00FF85]"
                                                                : p.status === "sent" || p.status === "sent_manual"
                                                                  ? "border-white text-white"
                                                                  : p.status === "queued"
                                                                    ? "border-[#FFCC00] text-[#FFCC00]"
                                                                    : "border-white/30 text-white/50"
                                                        }`}
                                                    >
                                                        {p.status.replace("_", " ")}
                                                    </span>
                                                    <div className="font-mono text-[9px] tracking-[0.2em] text-white/40">
                                                        {p.clicks} CLICKS
                                                    </div>
                                                </div>
                                            </div>
                                            <details className="mb-3">
                                                <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 cursor-pointer hover:text-white">
                                                    PREVIEW PITCH
                                                </summary>
                                                <div className="mt-3 p-3 bg-black/40 border border-white/10">
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-1">
                                                        SUBJECT: {p.subject}
                                                    </div>
                                                    <pre className="font-sans text-xs text-white/80 whitespace-pre-wrap leading-relaxed">{p.body}</pre>
                                                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 break-all">
                                                        CLAIM URL: {p.claim_url}
                                                    </div>
                                                </div>
                                            </details>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => copyPacket(p)}
                                                    className="btn-brutal text-[10px]"
                                                    data-testid={`outreach-copy-${p.code}`}
                                                >
                                                    COPY EMAIL
                                                </button>
                                                {p.status === "draft" && (
                                                    <>
                                                        <button
                                                            onClick={() => sendPacket(p.packet_id)}
                                                            className="btn-brutal btn-brutal-red text-[10px]"
                                                            data-testid={`outreach-send-${p.code}`}
                                                        >
                                                            <SendIcon size={11} /> SEND VIA RESEND
                                                        </button>
                                                        <button
                                                            onClick={() => markPacketSentManual(p.packet_id)}
                                                            className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 underline decoration-dotted underline-offset-4 hover:text-white"
                                                            data-testid={`outreach-mark-manual-${p.code}`}
                                                        >
                                                            MARK AS SENT MANUALLY
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {tab === "visitors" && (
                    <div data-testid="tab-admin-visitors" className="space-y-6">
                        {!visitors ? (
                            <div className="border-2 border-white/20 p-6 text-white/60 font-mono text-xs">
                                LOADING VISIBILITY STATS…
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 border-2 border-white/20">
                                    <div className="p-6 border-r-2 border-white/20">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="w-2 h-2 rounded-full bg-[#00FF85] animate-pulse" />
                                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
                                                LIVE NOW
                                            </div>
                                        </div>
                                        <div className="font-anton text-5xl text-white leading-none" data-testid="visitors-live-now">
                                            {visitors.live_now}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                                            LAST 5 MIN
                                        </div>
                                    </div>
                                    <div className="p-6 border-r-2 border-white/20">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                            VIEWS TODAY
                                        </div>
                                        <div className="font-anton text-5xl text-[#FF3B30] leading-none">
                                            {visitors.views_today}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                                            {visitors.new_sessions_today} NEW SESSIONS
                                        </div>
                                    </div>
                                    <div className="p-6 border-r-2 border-white/20">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                            GOOGLE ANALYTICS
                                        </div>
                                        <div className={`font-anton text-3xl leading-none ${visitors.ga4_configured ? "text-[#00FF85]" : "text-white/30"}`}>
                                            {visitors.ga4_configured ? "ON" : "OFF"}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                                            {visitors.ga4_configured ? "GA4 MEASURING" : "ADD GA4_MEASUREMENT_ID"}
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                            SEARCH CONSOLE
                                        </div>
                                        <div className={`font-anton text-3xl leading-none ${visitors.gsc_configured ? "text-[#00FF85]" : "text-white/30"}`}>
                                            {visitors.gsc_configured ? "ON" : "OFF"}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                                            {visitors.gsc_configured ? "VERIFIED META" : "ADD GOOGLE_SITE_VERIFICATION"}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="border-2 border-white/20 p-6">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
                                            TOP PAGES · LAST 7 DAYS
                                        </div>
                                        {visitors.top_pages_7d.length === 0 ? (
                                            <div className="font-mono text-xs text-white/40">
                                                NO PAGEVIEWS YET — START SHARING YOUR LINKS!
                                            </div>
                                        ) : (
                                            <ul className="space-y-2">
                                                {visitors.top_pages_7d.map((p, i) => (
                                                    <li key={i} className="flex items-baseline justify-between gap-2 font-mono text-xs">
                                                        <span className="truncate text-white/80">{p.path}</span>
                                                        <span className="font-anton text-base text-[#FF3B30]">{p.views}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="border-2 border-white/20 p-6">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
                                            TOP REFERRERS · LAST 7 DAYS
                                        </div>
                                        {visitors.top_referrers_7d.length === 0 ? (
                                            <div className="font-mono text-xs text-white/40">
                                                NO REFERRERS YET
                                            </div>
                                        ) : (
                                            <ul className="space-y-2">
                                                {visitors.top_referrers_7d.map((r, i) => (
                                                    <li key={i} className="flex items-baseline justify-between gap-2 font-mono text-xs">
                                                        <span className="truncate text-white/80">{r.host}</span>
                                                        <span className="font-anton text-base text-[#FFCC00]">{r.views}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                <div className="border-2 border-white/20 p-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
                                        DAILY VIEWS · LAST 7 DAYS
                                    </div>
                                    {visitors.daily_7d.length === 0 ? (
                                        <div className="font-mono text-xs text-white/40">
                                            ONCE PEOPLE START VISITING, A DAILY SPARK CHART WILL APPEAR HERE.
                                        </div>
                                    ) : (
                                        <div className="flex items-end gap-2 h-32">
                                            {visitors.daily_7d.map((d) => {
                                                const max = Math.max(...visitors.daily_7d.map((x) => x.views || 0), 1);
                                                const pct = ((d.views || 0) / max) * 100;
                                                return (
                                                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                                                        <div className="font-mono text-[10px] text-white/60">{d.views}</div>
                                                        <div
                                                            className="w-full bg-[#FF3B30]"
                                                            style={{ height: `${pct}%`, minHeight: "2px" }}
                                                        />
                                                        <div className="font-mono text-[9px] tracking-[0.15em] text-white/40">
                                                            {d.day.slice(5, 10)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {(!visitors.ga4_configured || !visitors.gsc_configured) && (
                                    <div className="border-2 border-[#FFCC00]/40 bg-[#FFCC00]/5 p-6">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                                            UNLOCK EXTERNAL VISIBILITY
                                        </div>
                                        <ul className="space-y-2 font-mono text-xs text-white/70">
                                            {!visitors.ga4_configured && (
                                                <li>
                                                    • Add <span className="text-white">GA4_MEASUREMENT_ID</span> in deploy
                                                    Secrets (looks like <span className="text-white">G-XXXXXXXXXX</span>) →
                                                    Google Analytics will start measuring source, geo, device.
                                                </li>
                                            )}
                                            {!visitors.gsc_configured && (
                                                <li>
                                                    • Add <span className="text-white">GOOGLE_SITE_VERIFICATION</span> in
                                                    deploy Secrets → verify in Search Console to see search impressions /
                                                    clicks per page.
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {tab === "newsletter" && (
                    <div data-testid="tab-admin-newsletter" className="space-y-6">
                        <div className="border-2 border-white/20 p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                                READER NEWSLETTER · WEEKLY BRIEF
                            </div>
                            <h2 className="font-anton text-3xl uppercase">Send the weekly to every subscriber</h2>
                            <p className="font-sans text-sm text-white/70 mt-2">
                                {ov?.newsletter.active_subscribers || 0} active subscribers will get this week's
                                digest. Dry-run first if you want a count before firing.
                            </p>
                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => fireNewsletter(true)}
                                    className="btn-brutal text-xs"
                                    data-testid="newsletter-dry"
                                >
                                    DRY RUN
                                </button>
                                <button
                                    onClick={() => fireNewsletter(false)}
                                    className="btn-brutal btn-brutal-red text-xs"
                                    data-testid="newsletter-fire"
                                >
                                    <Send size={12} /> FIRE NEWSLETTER
                                </button>
                            </div>
                        </div>

                        <div className="border-2 border-white/20 p-6">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                                FIRM DIGEST · WALL OF WINS
                            </div>
                            <h2 className="font-anton text-3xl uppercase">Push the weekly wall-of-wins to every firm</h2>
                            <p className="font-sans text-sm text-white/70 mt-2">
                                Featured solved cases + live blog-lead count emailed to every firm with
                                alerts_enabled. Drives them back to the portal.
                            </p>
                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => fireFirmDigest(true)}
                                    className="btn-brutal text-xs"
                                    data-testid="firmdigest-dry"
                                >
                                    DRY RUN
                                </button>
                                <button
                                    onClick={() => fireFirmDigest(false)}
                                    className="btn-brutal btn-brutal-red text-xs"
                                    data-testid="firmdigest-fire"
                                >
                                    <Send size={12} /> FIRE DIGEST
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "leads" && (
                    <div data-testid="tab-admin-leads">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-2">
                            LAST {leads.items.length} CAPTURED LEADS
                        </div>
                        <h2 className="font-anton text-3xl uppercase mb-4">Inbox</h2>
                        <div className="border-2 border-white/20">
                            {leads.items.length === 0 ? (
                                <div className="p-8 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                    NO LEADS YET.
                                </div>
                            ) : (
                                leads.items.map((l, i) => (
                                    <div
                                        key={l.lead_id}
                                        className="p-4 [&:not(:last-child)]:border-b-2 border-white/20"
                                        data-testid={`admin-lead-${i}`}
                                    >
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span
                                                    className={`font-anton text-lg uppercase border-2 px-2 ${l.tier === "S" ? "text-[#FF3B30] border-[#FF3B30]" : l.tier === "A" ? "text-[#FFCC00] border-[#FFCC00]" : "text-white border-white/40"}`}
                                                >
                                                    {l.tier}
                                                </span>
                                                <span className="font-anton text-lg uppercase">{l.name}</span>
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                                                    {l.state} · {l.case_type?.replace(/_/g, " ")} · ${l.price}
                                                </span>
                                            </div>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                                {(l.created_at || "").slice(0, 16)}
                                            </span>
                                        </div>
                                        <div className="font-sans text-sm text-white/85 italic mt-2">
                                            "{l.situation}"
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">
                                            {l.email} · {l.phone || "—"} ·{" "}
                                            <span className="text-[#FFCC00]">CLAIMED BY {(l.claimed_by_firm_ids || []).length}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {tab === "distribution" && (
                    <div data-testid="tab-admin-distribution" className="space-y-6">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                                    DISTRIBUTION CHANNELS · WHERE THE AUDIENCE COMES FROM
                                </div>
                                <h2 className="font-anton text-3xl uppercase mt-1">
                                    Push every page to every channel.
                                </h2>
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">
                                    EVERY CHANNEL IS FREE. PLUG IN ENV VARS POST-DEPLOY TO ACTIVATE.
                                </p>
                            </div>
                            <button
                                onClick={pingAll}
                                disabled={pingingAll}
                                className="btn-brutal btn-brutal-red text-xs"
                                data-testid="distribution-ping-all"
                            >
                                {pingingAll ? (
                                    <>
                                        <Loader2 size={12} className="animate-spin" /> PINGING...
                                    </>
                                ) : (
                                    <>
                                        <Radio size={12} /> PUSH ALL TO INDEXNOW + GOOGLE
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Public feed URLs */}
                        {dist && (dist.rss_feed_url || dist.sitemap_url) && (
                            <div className="border-2 border-white/20 p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-1 flex items-center gap-2">
                                        <Rss size={11} /> RSS FEED · SHARE WITH PARTNER ORGS
                                    </div>
                                    <a
                                        href={dist.rss_feed_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs text-white hover:text-[#FFCC00] break-all"
                                        data-testid="rss-feed-url"
                                    >
                                        {dist.rss_feed_url}
                                    </a>
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-1 flex items-center gap-2">
                                        <Map size={11} /> SITEMAP · SUBMIT TO GOOGLE SEARCH CONSOLE
                                    </div>
                                    <a
                                        href={dist.sitemap_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs text-white hover:text-[#FFCC00] break-all"
                                        data-testid="sitemap-url"
                                    >
                                        {dist.sitemap_url}
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Channel cards */}
                        {dist?.channels && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="distribution-channels">
                                {Object.entries(dist.channels).map(([k, ch]) => (
                                    <div
                                        key={k}
                                        className={`border-2 p-5 ${ch.configured ? "border-[#FFCC00]" : "border-white/20"}`}
                                        data-testid={`channel-${k}`}
                                    >
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                            <div className="font-anton text-xl uppercase">
                                                {k.replace(/_/g, " ")}
                                            </div>
                                            {ch.configured ? (
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] flex items-center gap-1">
                                                    <CheckCircle2 size={11} /> LIVE
                                                </span>
                                            ) : (
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1">
                                                    <AlertCircle size={11} /> NOT WIRED
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">
                                            FREE · {ch.covers}
                                        </div>
                                        <p className="font-sans text-xs text-white/70 leading-relaxed">
                                            {ch.setup}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "outbox" && (
                    <div data-testid="tab-admin-outbox">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                                    EMAIL OUTBOX · QUEUED
                                </div>
                                <h2 className="font-anton text-3xl uppercase">
                                    {outbox.items.length} emails waiting
                                </h2>
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                                    THESE ACCUMULATE WHEN RESEND_API_KEY ISN'T SET. ADD THE KEY THEN CLICK FLUSH.
                                </p>
                            </div>
                            <button
                                onClick={flushOutbox}
                                className="btn-brutal btn-brutal-red text-xs"
                                data-testid="flush-outbox"
                            >
                                <Send size={12} /> FLUSH OUTBOX
                            </button>
                        </div>
                        <div className="border-2 border-white/20">
                            {outbox.items.length === 0 ? (
                                <div className="p-8 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                    OUTBOX EMPTY.
                                </div>
                            ) : (
                                outbox.items.slice(0, 50).map((o, i) => (
                                    <div
                                        key={o.id}
                                        className="p-4 [&:not(:last-child)]:border-b-2 border-white/20"
                                        data-testid={`outbox-item-${i}`}
                                    >
                                        <div className="flex justify-between items-start gap-3 flex-wrap">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-anton text-lg uppercase truncate">
                                                    {o.subject}
                                                </div>
                                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">
                                                    TO {o.to} · FROM {o.from}
                                                </div>
                                            </div>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 whitespace-nowrap">
                                                {(o.created_at || "").slice(0, 16)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {tab === "transactions" && (
                    <div data-testid="tab-admin-tx">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                                LAST {txs.items.length} STRIPE TRANSACTIONS
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => runDrip(true)}
                                    data-testid="admin-drip-dryrun"
                                    className="border-2 border-white/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-white hover:text-black"
                                >
                                    DRIP · DRY-RUN
                                </button>
                                <button
                                    onClick={() => runDrip(false)}
                                    data-testid="admin-drip-run"
                                    className="border-2 border-[#FFCC00] text-[#FFCC00] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#FFCC00] hover:text-black"
                                >
                                    DRIP · FIRE D3/D7/D14/D21
                                </button>
                            </div>
                        </div>
                        <h2 className="font-anton text-3xl uppercase mb-4">The money trail</h2>
                        <div className="border-2 border-white/20">
                            {txs.items.length === 0 ? (
                                <div className="p-8 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                    NO TRANSACTIONS YET.
                                </div>
                            ) : (
                                txs.items.map((t, i) => (
                                    <div
                                        key={t.transaction_id || i}
                                        className="p-4 [&:not(:last-child)]:border-b-2 border-white/20 flex items-center justify-between gap-3"
                                        data-testid={`admin-tx-${i}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-anton text-xl uppercase">
                                                ${(t.amount || 0).toFixed(2)}{" "}
                                                <span className="text-white/40 text-sm">·</span>{" "}
                                                <span className="text-white/70 text-sm">
                                                    {(t.tier || "").toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1 truncate">
                                                {t.session_id || ""} · {(t.metadata?.purpose || "").replace(/_/g, " ")}
                                            </div>
                                        </div>
                                        <span
                                            className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border-2 ${
                                                t.payment_status === "paid"
                                                    ? "text-[#FFCC00] border-[#FFCC00]"
                                                    : t.payment_status === "refunded"
                                                    ? "text-white/40 border-white/30 line-through"
                                                    : "text-white/60 border-white/30"
                                            }`}
                                        >
                                            {(t.payment_status || "pending").toUpperCase()}
                                        </span>
                                        {t.payment_status === "paid" && t.session_id && (
                                            <button
                                                onClick={() => refundTx(t)}
                                                data-testid={`admin-tx-refund-${i}`}
                                                className="border-2 border-[#FF3B30] text-[#FF3B30] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#FF3B30] hover:text-white"
                                            >
                                                REFUND
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            <Footer disclaimer="" />
        </div>
    );
}

function KPI({ label, value, sub, accent = "#FF3B30" }) {
    return (
        <div className="p-5 border-white/20 [&:not(:nth-child(4n))]:border-r-2 [&:not(:nth-last-child(-n+1))]:border-b-2 md:[&:not(:nth-last-child(-n+4))]:border-b-2">
            <div
                className="font-mono text-[10px] uppercase tracking-[0.3em]"
                style={{ color: accent }}
            >
                {label}
            </div>
            <div className="font-anton text-4xl sm:text-5xl leading-none mt-2">{value}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">
                {sub}
            </div>
        </div>
    );
}
